import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Typography, Space, Row, Col, Spin, message } from 'antd';
import {
    StarFilled,
    UserOutlined,
    RobotOutlined,
    CrownOutlined,
    GiftOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    DownOutlined,
    UpOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import drEasyApi from '../../Api/Customapi';
import { useLanguage } from '../../context/LanguageContext';
import { Check, X } from 'lucide-react';
import { IoCloseOutline, IoCloseSharp } from 'react-icons/io5';
import { FaCheck } from 'react-icons/fa6';
import html2canvas from 'html2canvas';

const { Title, Text } = Typography;

export default function PlansSection() {
    const [realPlans, setRealPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [animateCards, setAnimateCards] = useState(false);
    const [expandedPlans, setExpandedPlans] = useState({});
    const [activeSlide, setActiveSlide] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const carouselRef = useRef(null);
    const sectionRef = useRef(null);
    const { t, isRTL } = useLanguage();

    const getUniqueFeatures = (currentPlan, allPlans) => {
        const currentLevel = currentPlan.level;
        const currentFeatures = currentPlan.features || [];

        if (currentLevel === 0) {
            return currentFeatures;
        }

        const previousPlansFeatures = allPlans
            .filter(plan => plan.level < currentLevel)
            .flatMap(plan => plan.features || [])
            .map(feature => feature.featureName || feature.name);

        const uniqueFeatures = currentFeatures.filter(feature =>
            !previousPlansFeatures.includes(feature.featureName || feature.name)
        );

        return uniqueFeatures;
    };

    const hasPreviousFeatures = (currentPlan, allPlans) => {
        return currentPlan.level > 0;
    };

    const getPlanIcon = (level) => {
        const icons = {
            0: <UserOutlined />,
            1: <RobotOutlined />,
            2: <CrownOutlined />
        };
        return icons[level] || <GiftOutlined />;
    };

    const getPlanColor = (level) => {
        const colors = {
            0: '#fbbf24',
            1: '#fbbf24',
            2: '#faad14'
        };
        return colors[level] || '#1890ff';
    };

    const getPreviousPlanName = (level) => {
        return level === 1 ? "Smart Start" : "Pro Clinic";
    };

    const togglePlanExpansion = (planId) => {
        setExpandedPlans(prev => ({
            ...prev,
            [planId]: !prev[planId]
        }));
    };

    // Fetch real plans from API
    const fetchPlans = async () => {
        setPlansLoading(true);
        try {
            const plansRes = await drEasyApi.get('/api/Plans/GetAllPlans', {
                headers:
                {
                    "Accept-Language": isRTL ? 'ar' : 'en'
                }
            });

            const sortedPlans = Array.isArray(plansRes.data)
                ? plansRes.data.sort((a, b) => a.level - b.level)
                : [];

            setRealPlans(sortedPlans);
        } catch (error) {
            console.error('Error fetching plans:', error);
            message.error(t('comingSoon.plans.loadFailed') || 'Failed to load plans');
            setRealPlans([]);
        }
        setPlansLoading(false);
    };

    useEffect(() => {
        fetchPlans();
        setTimeout(() => setAnimateCards(true), 200);
    }, [isRTL]);

    const handlePlanSelect = (planName) => {
        message.success(`${t('comingSoon.plans.selected') || 'You selected'} ${planName} ${t('comingSoon.plans.plan') || 'plan'}!`);
    };

    // Plan titles now pulled from translation keys via id, price/branches/users/spec stay as raw data
    const plans = [
        {
            id: 2,
            titleKey: 'comingSoon.plans.planTitles.basicClinic',
            titleFallback: "Basic Clinic",
            price: "8.200",
            branches: "1",
            users: "5",
            spec: "1",
            features: [1, 1, 1, 1, 0, 0, 0, 0, 0]
        },
        {
            id: 3,
            titleKey: 'comingSoon.plans.planTitles.professionalClinic',
            titleFallback: "Professional Clinic",
            price: "12.300",
            branches: "1",
            users: "15",
            spec: "4",
            features: [1, 1, 1, 1, 1, 1, 0, 0, 0]
        },
        {
            id: 4,
            titleKey: 'comingSoon.plans.planTitles.medicalCenter',
            titleFallback: "Medical Center",
            branches: "+3",
            price: "20.000",
            users: "+50",
            spec: "Unlimited",
            features: [1, 1, 1, 1, 1, 1, 1, 1, 1]
        },
    ].map(plan => ({ ...plan, title: t(plan.titleKey) || plan.titleFallback }));

    const features = [
        { id: 1, key: 'appointmentManagement', name: "Appointment Management" },
        { id: 2, key: 'patientCRM', name: "Patient CRM" },
        { id: 3, key: 'basicInvoicing', name: "Basic Invoicing" },
        { id: 4, key: 'onlineBooking', name: "Online Booking" },
        { id: 5, key: 'inventoryManagement', name: "Inventory Management" },
        { id: 6, key: 'advancedReports', name: "Advanced Reports" },
        { id: 7, key: 'aiChatbot', name: "AI Chatbot" },
        { id: 8, key: 'executiveDashboard', name: "Executive Dashboard" },
        { id: 9, key: 'apiIntegrations', name: "API Integrations" },
    ].map(feature => ({ ...feature, name: t(`comingSoon.plans.features.${feature.key}`) || feature.name }));

    // Mark the middle plan as "recommended" for a quick-scan visual anchor on mobile
    const recommendedIndex = 1;

    // Track which slide is centered so the dot indicator stays in sync while swiping
    const handleCarouselScroll = () => {
        const el = carouselRef.current;
        if (!el) return;
        const cardWidth = el.firstChild ? el.firstChild.offsetWidth + 16 : el.offsetWidth;
        const index = Math.round(el.scrollLeft / cardWidth);
        setActiveSlide(Math.min(Math.max(index, 0), plans.length - 1));
    };

    const scrollToSlide = (index) => {
        const el = carouselRef.current;
        if (!el) return;
        const cardWidth = el.firstChild ? el.firstChild.offsetWidth + 16 : el.offsetWidth;
        el.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
        setActiveSlide(index);
    };

    return (
        <div ref={sectionRef} className='relative md:min-h-screen'>

            <section className="py-12 sm:py-16 bg-primary_hover">
                <div className="mx-auto px-4 sm:px-6 lg:px-28 flex flex-col">
                    {/* Header */}
                    <div className="text-center mb-2 sm:mb-4 lg:mb-4">
                        <h1 className="text-secondary mb-1 md:mb-3 text-2xl md:text-5xl font-bold">
                            {t('comingSoon.plans.choosePlan') || 'Our Plans'}
                        </h1>
                        <h1 className="text-white/90 block mb-6 sm:mb-2 text-xl md:text-2xl">
                            {t('comingSoon.plans.freeTrialBanner') || 'Our Plans'}
                        </h1>
                    </div>

                    <Spin spinning={plansLoading} indicator={<LoadingOutlined style={{ fontSize: 48, color: 'white' }} spin />}>

                        {/* ---------- MOBILE / TABLET: swipeable self-contained cards ---------- */}
                        <div className="md:hidden">
                            <div
                                ref={carouselRef}
                                onScroll={handleCarouselScroll}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-4 px-4 scroll-smooth [&::-webkit-scrollbar]:hidden"
                                style={{ scrollbarWidth: 'none' }}
                            >
                                {plans.map((plan, index) => {
                                    const isRecommended = index === recommendedIndex;
                                    return (
                                        <div
                                            key={plan.id}
                                            className="snap-center shrink-0 w-[85vw] max-w-sm"
                                        >
                                            <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-b from-primary via-primary to-primary_hover  shadow-md `}>

                                                <div className={`bg-white text-primary_hover w-full text-center flex flex-col items-center justify-center p-4 ${isRecommended ? 'pt-8' : ''}`}>
                                                    <span className="text-3xl font-bold">{plan.price} <span className="text-base font-medium text-primary">{t('comingSoon.plans.currency') || 'EGP'}</span></span>
                                                    <span className="text-sm text-gray-500 mt-1">{t('comingSoon.plans.perYear') || 'per year'}</span>
                                                </div>

                                                <div className="p-5">
                                                    <p className="text-white font-semibold text-lg mb-3">{plan.title}</p>

                                                    {/* Quick-glance meta row */}
                                                    <div className="grid grid-cols-3 gap-2 mb-4">
                                                        <div className="bg-white/10 rounded-lg py-2 text-center">
                                                            <p className="text-white font-bold text-sm">{plan.branches}</p>
                                                            <p className="text-white/70 text-[11px]">{t('comingSoon.plans.branches') || 'Branches'}</p>
                                                        </div>
                                                        <div className="bg-white/10 rounded-lg py-2 text-center">
                                                            <p className="text-white font-bold text-sm">{plan.users}</p>
                                                            <p className="text-white/70 text-[11px]">{t('comingSoon.plans.users') || 'Users'}</p>
                                                        </div>
                                                        <div className="bg-white/10 rounded-lg py-2 text-center">
                                                            <p className="text-white font-bold text-xs truncate">{plan.spec}</p>
                                                            <p className="text-white/70 text-[11px]">{t('comingSoon.plans.specialty') || 'Specialty'}</p>
                                                        </div>
                                                    </div>

                                                    {/* Features paired directly with their labels */}
                                                    <ul className="space-y-2.5 mb-5">
                                                        {features.map((feature, fIndex) => {
                                                            const included = plan.features[fIndex] === 1;
                                                            return (
                                                                <li key={feature.id} className="flex items-center gap-2.5">
                                                                    {included ? (
                                                                        <FaCheck className="text-secondary text-base shrink-0" />
                                                                    ) : (
                                                                        <IoCloseSharp className="text-white/30 text-lg shrink-0" />
                                                                    )}
                                                                    <span className={included ? 'text-white text-sm' : 'text-white/40 text-sm line-through'}>
                                                                        {feature.name}
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>

                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Swipe position indicator */}
                            <div className="flex justify-center gap-2 mt-2">
                                {plans.map((plan, index) => (
                                    <button
                                        key={plan.id}
                                        aria-label={`${t('comingSoon.plans.goTo') || 'Go to'} ${plan.title}`}
                                        onClick={() => scrollToSlide(index)}
                                        className={`h-2 rounded-full transition-all ${activeSlide === index ? 'w-6 bg-secondary' : 'w-2 bg-white/40'}`}
                                    />
                                ))}
                            </div>
                            <p className="text-center text-white/60 text-xs mt-2"></p>
                        </div>

                        {/* ---------- DESKTOP / TABLET landscape: side-by-side comparison table ---------- */}
                        <Row className="mb-8 gap-5 justify-center hidden md:flex">
                            <Col sm={8} md={7} lg={6}>
                                <p className='text-secondary font-bold text-xl md:text-4xl h-24 mb-1 text-center'>{t('comingSoon.plans.sectionTitle') || 'Packages And Pricing'}</p>
                                <div className='p-1 text-base md:text-xl'>
                                    <p className='text-white border-b border-white/40 h-10 flex items-center justify-center '>{t('comingSoon.plans.branches') || 'Branches'}</p>
                                    <p className='text-white border-b border-white/40 h-10 flex items-center justify-center'>{t('comingSoon.plans.users') || 'Users'}</p>
                                    <p className='text-white border-b border-white/40 h-10 flex items-center justify-center'>{t('comingSoon.plans.specialty') || 'Specialty'}</p>

                                    {features.map((feature, idx) => (
                                        <p key={feature.id} className={`text-white ${idx != features.length - 1 && "border-b border-white/40"} h-10 flex items-center justify-center`}>{feature.name}</p>
                                    ))}
                                </div>
                            </Col>
                            {plans.map((plan, index) => {
                                const isRecommended = index === recommendedIndex;
                                return (
                                    <Col md={7} lg={5} key={plan.id}>
                                        <div className={`bg-gradient-to-b from-primary via-primary  to-primary_hover rounded-lg  relative`}>

                                            <div className="bg-white h-28 p-1 py-3.5 rounded-t-lg mb-1 text-primary w-full text-center flex flex-col items-center justify-center">
                                                <span className='flex justify-center mt-3'>
                                                    <p className='text-2xl md:text-4xl font-semibold text-primary_hover'>{plan.price}</p> {t('comingSoon.plans.currency') || 'EGP'}
                                                </span>
                                                <span className='text-base md:text-lg'>{t('comingSoon.plans.annual') || 'Annual'}</span>
                                            </div>
                                            <div className='bg-gradient-to-b from-primary via-primary to-primary_hover rounded-md p-1 flex flex-col items-center justify-center'>
                                                <span className='h-10 flex justify-center items-center text-white font-medium text-lg'>{plan.branches}</span>
                                                <span className='h-10 flex justify-center items-center text-white font-medium text-lg'>{plan.users}</span>
                                                <span className='h-10 flex justify-center items-center text-white font-medium text-lg'>{plan.spec}</span>
                                                {plan.features.map((f, i) => (
                                                    <span key={i} className='h-10 flex justify-center items-center'>
                                                        {f === 1 ? <FaCheck className='text-secondary text-2xl' /> : <IoCloseOutline className='text-white/60 -600 text-3xl' />}
                                                    </span>
                                                ))}
                                                <span
                                                    className='bg-white p-1.5 px-4 mt-3 text-primary_hover rounded-full font-semibold transition'
                                                >
                                                    {plan.title}
                                                </span>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Spin>

                    {/* Loading State */}
                    {plansLoading && (
                        <div className="text-center text-white py-8" style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>
                            {t('comingSoon.plans.loadingPlans') || 'Loading plans...'}
                        </div>
                    )}

                    {/* Empty State */}
                    {!plansLoading && realPlans.length === 0 && (
                        <div className="text-center text-white py-12">
                            <GiftOutlined style={{ fontSize: 'clamp(32px, 6vw, 48px)', marginBottom: '16px' }} />
                            <div style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>{t('comingSoon.plans.noPlans') || 'No plans available'}</div>
                            <div style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', opacity: 0.8, marginTop: '8px' }}>
                                {t('comingSoon.plans.checkBack') || 'Please check back later'}
                            </div>
                        </div>
                    )}

                    {/* Support Note */}
                    <div className="relative bg-white w-full md:w-fit    md:mx-24 md:self-end md:justify-end px-5 py-3 rounded-lg md:rounded-3xl  shadow-sm">
                        <span className="absolute -top-3 inline-block bg-secondary text-white px-3 py-1 text-xs font-semibold rounded-full mb-1 md:mb-4 tracking-wide">
                            {t('comingSoon.plans.extra.badge') || 'EXTRA'}
                        </span>
                        <ul className="grid md:grid-cols-2 gap-2 md:gap-x-12   ">
                            <li className="flex items-center justify- py-1 md:py-2 text-sm">
                                <span className="text-gray-600">{t('comingSoon.plans.extra.additionalUser') || 'Additional User'} : </span>
                                <span className="font-semibold text-primary_hover"> {t('comingSoon.plans.extra.additionalUserPrice') || '100 EGP/year'}</span>
                            </li>
                            <li className="flex items-center justify- py-1 md:py-2 text-sm">
                                <span className="text-gray-600">{t('comingSoon.plans.extra.additionalBranch') || 'Additional Branch'} : </span>
                                <span className="font-semibold text-primary_hover"> {t('comingSoon.plans.extra.additionalBranchPrice') || '300 EGP/year'}</span>
                            </li>
                            <li className="flex items-center justify- py-1 md:py-2 text-sm">
                                <span className="text-gray-600">{t('comingSoon.plans.extra.additionalSpecialty') || 'Additional Specialty'} : </span>
                                <span className="font-semibold text-primary_hover"> {t('comingSoon.plans.extra.additionalSpecialtyPrice') || '5,000 EGP/year'}</span>
                            </li>
                            <li className="flex items-center justify- py-1 md:py-2 text-sm">
                                <span className="text-gray-600">{t('comingSoon.plans.extra.website') || 'Website'} : </span>
                                <span className="font-semibold text-primary_hover"> {t('comingSoon.plans.extra.websitePrice') || '3,500 SAR'}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}